import { ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { Platform, AlertController, MenuController } from '@ionic/angular';
import { Router, NavigationEnd, NavigationError, NavigationStart } from '@angular/router';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor, SystemBars, SystemBarsStyle } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { App as CapacitorApp } from '@capacitor/app';
import { environment } from 'src/environments/environment';
import { UserService } from './service/user.service';
import { HttpsService } from './service/https.service';
import { openExternalUrl, shareText } from './utils/external-actions';
import { AccessControlService } from './service/access-control.service';
import { normalizeSubscriptionPlan, subscriptionPlanName } from './config/subscription-plans';
import { AuthorizationService } from './service/authorization.service';
import { RevenueCatService } from './service/revenuecat.service';
import { OfflineSyncService } from './offline/offline-sync.service';
import { ClienteRepositoryService } from './service/cliente-repository.service';
import { ObraRepositoryService } from './service/obra-repository.service';
import { OfflineDatabaseService } from './offline/offline-database.service';
import { ImageCacheService } from './service/image-cache.service';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {

  user: any = null;
  isNativeApp = Capacitor.isNativePlatform();
  isMobileWebBlocked = false;
  dominioWebCopiado = false;
  readonly dominioWebDesktop = 'app.construfacil.net.br';
  showAppMenu = false;
  rotaAtual = '';
  appInicializando = true;
  avisoPermissoesExpiradasAberto = false;
  modulos = environment.modulos;
  private readonly publicRoutes = ['/login', '/register', '/recover', '/password-update', '/update'];
  private pendingPushToken = '';
  private savedPushToken = '';
  private finalizacaoVisualEmAndamento = false;
  private splashNativaOcultada = false;
  private splashNativaOcultando = false;
  private splashContingenciaId?: number;
  private avisoExpiracaoTimerId?: number;
  private usuarioSincronizadoId = 0;
  private sincronizacaoUsuarioEmAndamento = false;
  private verificacaoAssinaturaExpiradaEmAndamento = false;
  private monitorAssinaturaExpiradaTimerId?: number;
  private aplicativoEmPrimeiroPlano = true;
  private dadosLocaisUsuarioId = 0;
  private sessaoInvalidaEmAndamento = false;
  private readonly intervaloMonitorAssinaturaExpiradaMs = 30000;
  private readonly splashContingenciaMs = 10000;

  get nomePlanoAtual(): string {
    const planoNormalizado = normalizeSubscriptionPlan(this.user?.subscription_plan);
    if (planoNormalizado && planoNormalizado !== 'trial') {
      return subscriptionPlanName(planoNormalizado);
    }

    if (Number(this.user?.user_paid || 0) >= 1) {
      return String(this.user?.plano || '').trim() || 'Premium';
    }

    if (normalizeSubscriptionPlan(this.user?.subscription_plan) === 'trial' && this.testeGratuitoAtivo()) {
      return 'Teste gratuito';
    }

    return 'Gratuito';
  }

  get planoContratadoAtivo(): boolean {
    return Number(this.user?.user_paid || 0) >= 1;
  }

  get planoContratadoExpirado(): boolean {
    const plano = normalizeSubscriptionPlan(this.user?.subscription_plan);
    return !this.planoContratadoAtivo && !!plano && plano !== 'trial';
  }

  get vencimentoPlanoAtual(): string {
    const valor = this.user?.paid_end_date_raw || this.user?.paid_end_date;
    if (!valor) {
      return '';
    }

    const texto = String(valor).trim();
    const dataBanco = texto.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
    if (dataBanco) {
      const hora = dataBanco[4] && dataBanco[5] ? ` ${dataBanco[4]}:${dataBanco[5]}` : '';
      return `${dataBanco[3]}/${dataBanco[2]}/${dataBanco[1]}${hora}`;
    }

    const dataBrasileira = texto.match(/(\d{2}\/\d{2}\/\d{4})(?:\s+(\d{2}:\d{2}))?/);
    if (dataBrasileira) {
      return `${dataBrasileira[1]}${dataBrasileira[2] ? ` ${dataBrasileira[2]}` : ''}`;
    }

    return texto;
  }

  get contaComPermissoesDelegadas(): boolean {
    return !!this.user?.is_team_member || !!this.user?.is_client_user;
  }

  get permissoesDelegadasExpiradas(): boolean {
    if (!this.contaComPermissoesDelegadas || !this.user?.access_expires_at) return false;
    const vencimento = this.timestampAcesso(this.user.access_expires_at);
    return vencimento !== null && vencimento < Date.now();
  }

  get validadePermissoesDelegadas(): string {
    return this.formatarDataAcesso(this.user?.access_expires_at);
  }

  get tipoContaDelegada(): string {
    return this.user?.is_client_user ? 'cliente' : 'membro da equipe';
  }

  get nomeAdministradorAcesso(): string {
    return String(this.user?.access_admin_name || 'administrador').trim();
  }

  constructor(
    private alertCtrl: AlertController,
    private platform: Platform,
    private router: Router,
    private alertController: AlertController,
    private menuCtrl: MenuController,
    private userService: UserService,
    private https: HttpsService,
    private accessControl: AccessControlService,
    private authorization: AuthorizationService,
    private revenuecat: RevenueCatService,
    private offlineSync: OfflineSyncService,
    private clienteRepository: ClienteRepositoryService,
    private obraRepository: ObraRepositoryService,
    private offlineDatabase: OfflineDatabaseService,
    private imageCache: ImageCacheService,
    private cdr: ChangeDetectorRef,
  ) {
    this.initializeApp();
  }

  pode(permissao: string): boolean {
    return this.authorization.can(permissao);
  }

  rotaMenuAtiva(...rotas: string[]): boolean {
    const rotaAtual = this.rotaAtual.split('?')[0].split('#')[0];
    return rotas.some((rota) => rotaAtual === rota || rotaAtual.startsWith(`${rota}/`));
  }

  private testeGratuitoAtivo(): boolean {
    const valor = this.user?.trial_end_date;
    if (!valor) {
      return false;
    }

    const dataFinal = new Date(String(valor).replace(' ', 'T'));
    return !Number.isNaN(dataFinal.getTime()) && dataFinal.getTime() >= Date.now();
  }

  async listOrcamento() {
    this.router.navigate(['/orcamentos']);
  }

  async fecharMenu() {
    await this.menuCtrl.close().catch(() => undefined);
  }

  async abrirArmazenamento(): Promise<void> {
    await this.fecharMenu();
    await this.router.navigateByUrl('/armazenamento', { replaceUrl: true });
  }

  async abrirPersonalizacao(): Promise<void> {
    await this.fecharMenu();
    const podeAcessar = await this.accessControl.canUsePlanFeature(
      'A personalização do aplicativo',
      'obras'
    );
    if (!podeAcessar) return;

    await this.router.navigateByUrl('/personalizacao-app');
  }

  async listObraHistorico() {
    const podeAcessar = await this.accessControl.canUseFeature('o histórico da obra');
    if (!podeAcessar) return;

    this.router.navigate(['/list-obra-historico']);
  }

  async listUserMaterialColecao() {
    this.router.navigate(['/user-material-colecao']);
  }

  politica() {
    openExternalUrl('https://construfacil.net.br/politica-de-privacidade.html');
  }

  termo() {
    openExternalUrl('https://construfacil.net.br/termos-e-condicoes.html');
  }

  log_update() {
    openExternalUrl('https://construfacil.net.br/log-update.html');
  }

  app_rate() {
    openExternalUrl('https://play.google.com/store/apps/details?id=br.com.construfacil.lista');
  }

  abrirAppMobile() {
    openExternalUrl('https://play.google.com/store/apps/details?id=br.com.construfacil.lista');
  }

  async copiarDominioWeb(): Promise<void> {
    try {
      await navigator.clipboard.writeText(`https://${this.dominioWebDesktop}`);
      this.dominioWebCopiado = true;

      window.setTimeout(() => {
        this.dominioWebCopiado = false;
      }, 2500);
    } catch (error) {
      
      this.dominioWebCopiado = false;
    }
  }

  async shareApp() {
    const shareUrl = Capacitor.isNativePlatform()
      ? 'https://play.google.com/store/apps/details?id=br.com.construfacil.lista'
      : 'https://app.construfacil.net.br';

    const shareData = {
      title: 'Construfácil - Listas e Orçamentos',
      text: 'Crie listas e orçamentos profissionais e envie para seus clientes.',
      url: shareUrl,
    };

    await shareText(shareData);
  }

  async initializeApp() {
    await this.platform.ready().then(() => {

      if (!Capacitor.isNativePlatform()) {
        return;
      }

      SystemBars.setStyle({ style: SystemBarsStyle.Light });

      // Evita que uma falha de navegação mantenha a tela de abertura indefinidamente.

    }).catch((error) => void 0);
  }

  ngOnInit() {
    this.updateMobileWebBlock();
    this.updateMenuVisibility(this.router.url);

    if (Capacitor.isNativePlatform()) {
      void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        this.aplicativoEmPrimeiroPlano = isActive;
        this.atualizarMonitorAssinaturaExpirada(this.userService.user);
        if (isActive && this.user) {
          void this.authorization.refresh(true);
          void this.sincronizarStatusAcesso(true);
          void this.offlineSync.syncNow();
          void this.sincronizarCachesLocais();
          this.atualizarAvisoPermissoesExpiradas(this.user);
        }
      });
      this.platform.backButton.subscribeWithPriority(10, () => {
        // Verifica se está na página de login.
        if (this.router.url === '/login' || this.router.url === '/' || this.router.url === '/password-update') {
          this.confirmExitApp();
        } else {
          window.history.back();
        }
      });
    } else if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        this.aplicativoEmPrimeiroPlano = !document.hidden;
        this.atualizarMonitorAssinaturaExpirada(this.userService.user);
        if (this.aplicativoEmPrimeiroPlano && this.user) {
          void this.sincronizarStatusAcesso(true);
          void this.sincronizarCachesLocais();
        }
      });
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => void this.sincronizarCachesLocais());
      window.addEventListener('authorization-invalid', () => void this.tratarSessaoInvalida());
    }

    this.userService.user$.subscribe(user => {
      this.user = user;
      this.atualizarMonitorAssinaturaExpirada(user);
      this.atualizarAvisoPermissoesExpiradas(user);
      if (user) {
        this.accessControl.updateAccessStateFromCurrentUser();
        void this.authorization.load();
        void this.sincronizarStatusAcesso();
        void this.inicializarDadosLocais(Number(user?.user_id || user?.id || 0));
      } else {
        this.dadosLocaisUsuarioId = 0;
        void this.offlineSync.stop();
      }
      this.cdr.markForCheck();
      this.updateMenuVisibility(this.router.url);
      if (user && this.pendingPushToken) {
        this.savePushToken(this.pendingPushToken);
      }
    });

    this.authorization.context$.subscribe((context) => {
      const userId = Number(this.user?.user_id || this.user?.id || 0);
      if (!context?.userId || context.userId !== userId) return;
      // Uma nova revisão de permissões invalida e remove fisicamente o snapshot anterior de obras.
      void this.obraRepository.initializeForUser(userId).catch(() => undefined);
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.updateMenuVisibility(event.url);
      }

      if (event instanceof NavigationEnd) {
        this.updateMenuVisibility(event.urlAfterRedirects || event.url);
        void this.finalizarInicializacaoVisual();

        if (event.url === '/') {
          this.userService.loadUser();
        }
      }

      if (event instanceof NavigationError) {
        void this.liberarSplashEmContingencia('A primeira navegação terminou com erro.');
      }
    });

    // Mantém uma interface visível mesmo se uma rota falhar ao carregar.
    this.agendarContingenciaDaSplash();

    if (Capacitor.isNativePlatform()) {
      this.setupPushNotifications();
    }
  }

  private async inicializarDadosLocais(userId: number): Promise<void> {
    if (userId <= 0 || this.dadosLocaisUsuarioId === userId) return;
    this.dadosLocaisUsuarioId = userId;

    try {
      await this.offlineSync.startForUser(userId);
      await Promise.all([
        this.clienteRepository.initializeForUser(userId),
        this.obraRepository.initializeForUser(userId),
      ]);
    } catch {
      // A ausência de conexão não impede o login nem o uso dos dados já armazenados.
    }
  }

  private async sincronizarCachesLocais(): Promise<void> {
    const userId = Number(this.user?.user_id || this.user?.id || 0);
    if (userId <= 0) return;
    try {
      await Promise.all([
        this.clienteRepository.initializeForUser(userId),
        this.obraRepository.initializeForUser(userId),
      ]);
    } catch {
      // A próxima retomada, reconexão ou abertura da tela fará uma nova tentativa.
    }
  }

  private async tratarSessaoInvalida(): Promise<void> {
    if (this.sessaoInvalidaEmAndamento) return;
    this.sessaoInvalidaEmAndamento = true;
    const userId = Number(this.user?.user_id || this.user?.id || 0);

    try {
      if (userId > 0) {
        await this.offlineDatabase.initializeForUser(userId).catch(() => false);
        await this.obraRepository.invalidateObras(userId).catch(() => undefined);
        await this.clienteRepository.invalidateClientes(userId).catch(() => undefined);
        await this.imageCache.clear().catch(() => undefined);
      }

      await this.offlineSync.stop().catch(() => undefined);
      if (userId > 0) {
        await this.offlineDatabase.deleteForUser(userId).catch(() => undefined);
      }
      await this.authorization.invalidate().catch(() => undefined);
      await this.userService.logout();
      this.dadosLocaisUsuarioId = 0;
      await this.router.navigate(['/login'], { replaceUrl: true });

      if (userId > 0) {
        const alert = await this.alertCtrl.create({
          header: 'Sessão encerrada',
          message: 'Seu acesso não é mais válido. O cache local de clientes, obras e imagens foi removido com segurança.',
          cssClass: ['custom-alert', 'alert-confirm'],
          buttons: [{ text: 'Entendi', role: 'cancel' }]
        });
        await alert.present();
      }
    } finally {
      this.sessaoInvalidaEmAndamento = false;
    }
  }

  fecharAvisoPermissoesExpiradas(): void {
    const chave = this.chaveAvisoExpiracao(this.user);
    if (chave && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(chave, '1');
    }
    this.avisoPermissoesExpiradasAberto = false;
  }

  private async sincronizarStatusAcesso(forcar = false): Promise<void> {
    const usuarioId = Number(this.user?.user_id || 0);
    if (!usuarioId || this.sincronizacaoUsuarioEmAndamento || (!forcar && this.usuarioSincronizadoId === usuarioId)) {
      return;
    }
    this.usuarioSincronizadoId = usuarioId;
    this.sincronizacaoUsuarioEmAndamento = true;
    try {
      await this.userService.syncUserStatus();
      await this.verificarAssinaturaExpiradaNaLoja();
    } finally {
      this.sincronizacaoUsuarioEmAndamento = false;
    }
  }

  private async verificarAssinaturaExpiradaNaLoja(): Promise<void> {
    const usuario = this.userService.user;

    if (!this.deveMonitorarAcessoEncerrado(usuario) || !this.revenuecat.isAvailable() || this.verificacaoAssinaturaExpiradaEmAndamento) {
      return;
    }

    const userToken = await this.userService.getUserToken();
    if (!userToken) return;

    this.verificacaoAssinaturaExpiradaEmAndamento = true;
    try {
      await this.revenuecat.setup(userToken);
      const customerInfo = await this.revenuecat.refreshCustomerInfoFromStore();
      if (!this.revenuecat.hasActivePremiumAccess(customerInfo.customerInfo)) {
        return;
      }

      // A sincronização da loja pode acionar o webhook; faz novas leituras curtas do servidor.
      for (let tentativa = 0; tentativa < 3; tentativa++) {
        if (tentativa > 0) {
          await new Promise<void>((resolve) => window.setTimeout(resolve, 1200));
        }

        await this.userService.syncUserStatus();
        if (Number(this.userService.user?.user_paid || 0) >= 1) {
          await this.authorization.refresh(true);
          break;
        }
      }
    } catch (error) {
      // Sem internet ou indisponibilidade da loja não encerra a sessão do usuário.
    } finally {
      this.verificacaoAssinaturaExpiradaEmAndamento = false;
    }
  }

  private atualizarMonitorAssinaturaExpirada(user: any): void {
    if (!this.deveMonitorarAcessoEncerrado(user) || !this.aplicativoEmPrimeiroPlano) {
      this.pararMonitorAssinaturaExpirada();
      return;
    }

    if (this.monitorAssinaturaExpiradaTimerId !== undefined) return;
    this.monitorAssinaturaExpiradaTimerId = window.setTimeout(() => {
      this.monitorAssinaturaExpiradaTimerId = undefined;
      void this.consultarServidorAssinaturaExpirada();
    }, this.intervaloMonitorAssinaturaExpiradaMs);
  }

  private pararMonitorAssinaturaExpirada(): void {
    if (this.monitorAssinaturaExpiradaTimerId === undefined) return;
    window.clearTimeout(this.monitorAssinaturaExpiradaTimerId);
    this.monitorAssinaturaExpiradaTimerId = undefined;
  }

  private async consultarServidorAssinaturaExpirada(): Promise<void> {
    if (!this.aplicativoEmPrimeiroPlano || !this.userService.user) return;

    await this.userService.syncUserStatus();
    if (Number(this.userService.user?.user_paid || 0) >= 1) {
      this.pararMonitorAssinaturaExpirada();
      this.user = this.userService.user;
      this.accessControl.updateAccessStateFromCurrentUser();
      await this.authorization.refresh(true);
      this.cdr.detectChanges();
      return;
    }

    this.atualizarMonitorAssinaturaExpirada(this.userService.user);
  }

  private deveMonitorarAcessoEncerrado(user: any): boolean {
    if (!user || Number(user?.user_paid || 0) >= 1) return false;

    const plano = normalizeSubscriptionPlan(user?.subscription_plan);
    const assinaturaExpirada = !!plano && plano !== 'trial';
    if (assinaturaExpirada) return true;

    const terminoTeste = new Date(String(user?.trial_end_date || '').replace(' ', 'T')).getTime();
    return Number.isFinite(terminoTeste) && terminoTeste < Date.now();
  }

  private atualizarAvisoPermissoesExpiradas(user: any): void {
    if (this.avisoExpiracaoTimerId !== undefined) {
      window.clearTimeout(this.avisoExpiracaoTimerId);
      this.avisoExpiracaoTimerId = undefined;
    }
    if (!user || (!user.is_team_member && !user.is_client_user) || !user.access_expires_at) {
      this.avisoPermissoesExpiradasAberto = false;
      return;
    }
    const vencimento = this.timestampAcesso(user.access_expires_at);
    if (vencimento === null || vencimento >= Date.now()) {
      this.avisoPermissoesExpiradasAberto = false;
      if (vencimento !== null) {
        const espera = Math.min(Math.max(1000, vencimento - Date.now() + 1000), 2147483647);
        this.avisoExpiracaoTimerId = window.setTimeout(() => {
          this.atualizarAvisoPermissoesExpiradas(this.user);
        }, espera);
      }
      return;
    }
    const chave = this.chaveAvisoExpiracao(user);
    this.avisoPermissoesExpiradasAberto = typeof sessionStorage === 'undefined'
      || sessionStorage.getItem(chave) !== '1';
  }

  private chaveAvisoExpiracao(user: any): string {
    const usuarioId = Number(user?.user_id || 0);
    const vencimento = String(user?.access_expires_at || '').replace(/[^0-9]/g, '');
    return usuarioId && vencimento ? `aviso-permissoes-expiradas:${usuarioId}:${vencimento}` : '';
  }

  private timestampAcesso(valor: any): number | null {
    const texto = String(valor || '').trim();
    if (!texto) return null;
    const timestamp = new Date(texto.replace(' ', 'T')).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  private formatarDataAcesso(valor: any): string {
    const texto = String(valor || '').trim();
    const dataBanco = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dataBanco) return `${dataBanco[3]}/${dataBanco[2]}/${dataBanco[1]}`;
    return '';
  }

  private async finalizarInicializacaoVisual(): Promise<void> {
    if (!this.appInicializando || this.finalizacaoVisualEmAndamento) {
      return;
    }

    this.finalizacaoVisualEmAndamento = true;
    this.appInicializando = false;
    this.cdr.detectChanges();

    await this.aguardarRenderizacaoDaPrimeiraTela();
    await this.ocultarSplashNativa();
  }

  private agendarContingenciaDaSplash(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    this.splashContingenciaId = window.setTimeout(() => {
      void this.liberarSplashEmContingencia('A primeira navegação não foi concluída no tempo de segurança.');
    }, this.splashContingenciaMs);
  }

  private async liberarSplashEmContingencia(motivo: string): Promise<void> {
    if (!Capacitor.isNativePlatform() || this.splashNativaOcultada) {
      return;
    }

    
    await this.aguardarRenderizacaoDaPrimeiraTela();
    await this.ocultarSplashNativa();
  }

  private aguardarRenderizacaoDaPrimeiraTela(): Promise<void> {
    return new Promise(resolve => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });
  }

  private async ocultarSplashNativa(): Promise<void> {
    if (!Capacitor.isNativePlatform() || this.splashNativaOcultada || this.splashNativaOcultando) {
      return;
    }

    this.splashNativaOcultando = true;

    try {
      await this.platform.ready();
      // Remove a janela nativa imediatamente para que ela não permaneça sobre a WebView
      // durante mudanças de tamanho, como na abertura do teclado.
      await SplashScreen.hide({ fadeOutDuration: 0 });
      this.splashNativaOcultada = true;
      this.limparContingenciaDaSplash();
    } catch (error) {
      
    } finally {
      this.splashNativaOcultando = false;
    }
  }

  private limparContingenciaDaSplash(): void {
    if (this.splashContingenciaId === undefined) {
      return;
    }

    window.clearTimeout(this.splashContingenciaId);
    this.splashContingenciaId = undefined;
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updateMobileWebBlock();
    this.updateMenuVisibility(this.router.url);
  }

  private updateMobileWebBlock() {
    this.isMobileWebBlocked = this.shouldBlockMobileWeb();
  }

  private shouldBlockMobileWeb(): boolean {
    if (!environment.production || Capacitor.isNativePlatform()) {
      return false;
    }

    const navegador = navigator as Navigator & {
      userAgentData?: { mobile?: boolean };
    };
    const navegadorMovel = navegador.userAgentData?.mobile === true
      || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(navegador.userAgent);
    const viewportMovel = window.matchMedia('(max-width: 1024px)').matches;
    const menorDimensaoDaTela = Math.min(window.screen.width, window.screen.height);
    const dispositivoDeToque = navegador.maxTouchPoints > 0
      || window.matchMedia('(hover: none) and (pointer: coarse)').matches;

    // A dimensão visível cobre navegadores que ocultam a identificação móvel,
    // inclusive quando o usuário solicita a versão para computador no celular.
    return navegadorMovel || viewportMovel || (dispositivoDeToque && menorDimensaoDaTela <= 1024);
  }

  private updateMenuVisibility(url: string) {
    this.rotaAtual = String(url || '').split('?')[0].split('#')[0];

    if (this.isMobileWebBlocked) {
      this.showAppMenu = false;
      this.menuCtrl.close().catch(() => undefined);
      this.menuCtrl.enable(false).catch(() => undefined);
      return;
    }

    const routePath = '/' + String(url || '')
      .split('?')[0]
      .split('#')[0]
      .replace(/^\/+/, '')
      .split('/')[0];

    this.showAppMenu = routePath !== '/' || !!this.user;
    this.showAppMenu = this.showAppMenu && !this.publicRoutes.includes(routePath);

    if (!this.showAppMenu) {
      this.menuCtrl.close().catch(() => undefined);
      this.menuCtrl.enable(false).catch(() => undefined);
      return;
    }

    this.menuCtrl.enable(true).catch(() => undefined);
  }

  async setupPushNotifications() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    await this.addListeners();
    await this.registerNotifications();
    this.getDeliveredNotifications();
  }

  addListeners = async () => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    await PushNotifications.addListener('registration', token => {
      
      this.savePushToken(token.value);
    });

    await PushNotifications.addListener('registrationError', err => {
      
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (actionPerformed) => {
      const data = actionPerformed?.notification?.data;
      const route = data?.route;

      if (data?.type === 'orcamento_aprovado' && data?.orcamento_id) {
        const sub = this.userService.user$.subscribe(user => {
          if (user) {
            this.router.navigate(['/orcamento-view', { orcamento_id: data.orcamento_id }]);
            sub.unsubscribe();
          }
        });
        this.userService.loadUser();
      } else if (route) {
        const sub = this.userService.user$.subscribe(user => {
          if (user) {
            this.router.navigate([route]);
            sub.unsubscribe(); // Evita múltiplas execuções.
          }
        });
        // Caso o usuário ainda não esteja carregado, força o carregamento.
        this.userService.loadUser();
      } else if (data.action === 'rate') {
        // Abre o Google Play para avaliação.
        const appId = 'br.com.construfacil.lista'; // substitua pelo applicationId do seu app
        openExternalUrl(`https://play.google.com/store/apps/details?id=${appId}`);
      }
    });

  }

  async savePushToken(token: string) {
    if (!token) {
      return;
    }

    this.pendingPushToken = token;
    if (this.savedPushToken === token) {
      return;
    }

    const userToken = await this.userService.getUserToken();
    if (!userToken) {
      return;
    }

    const body = {
      callback: 'save_push_token',
      token,
      platform: this.platform.is('ios') ? 'ios' : (this.platform.is('android') ? 'android' : 'web')
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(body));

    this.https.post(formData, 'user.php').then((res: any) => {
      if (res?.success) {
        this.savedPushToken = token;
        this.pendingPushToken = '';
      }
    }).catch(error => {
      
    });
  }

  async registerNotifications() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      throw new Error('User denied permissions!');
    }
    await PushNotifications.register();
  }

  async getDeliveredNotifications() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const notificationList = await PushNotifications.getDeliveredNotifications();
    
  }

  ionViewDidEnter() {
  }

  logout() {
    this.userService.logout(); // limpa BehaviorSubject
    this.router.navigate(['/login']);
  }

  async confirmExitApp() {
    const alert = await this.alertController.create({
      header: 'Sair do aplicativo?',
      message: 'Tem certeza de que deseja sair do aplicativo?',
      cssClass: 'custom-alert alert-confirm',
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel'
        },
        {
          text: 'Sair',
          handler: () => {
            navigator['app']?.exitApp?.(); // Fecha o aplicativo
          }
        }
      ]
    });
    await alert.present();
  }



}
